import React, { useState, useEffect } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Progress } from '../components/ui/progress';
import { ArrowLeft, Target, TrendingUp, Zap } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import { useLanguage } from '../i18n/LanguageContext';

const GoalsPage = () => {
  const navigate = useNavigate();
  const { t, language } = useLanguage();
  const [goals, setGoals] = useState(null);
  const [selectedGoal, setSelectedGoal] = useState(50);
  const [saving, setSaving] = useState(false);
  const [loading, setLoading] = useState(true);

  const goalOptions = [10, 20, 30, 50, 75, 100, 150, 200];

  useEffect(() => {
    loadGoals();
  }, []);

  const loadGoals = async () => {
    try {
      const token = localStorage.getItem('spirify_token');
      const response = await fetch(
        `${window.location.origin}/api/gamification/goals`,
        {
          headers: {
            'Authorization': `Bearer ${token}`
          }
        }
      );

      if (response.ok) {
        const data = await response.json();
        setGoals(data);
        setSelectedGoal(data.daily_goal);
        setLoading(false);
      }
    } catch (error) {
      console.error('Error loading goals:', error);
      setLoading(false);
    }
  };

  const handleSaveGoal = async () => {
    setSaving(true);
    try {
      const token = localStorage.getItem('spirify_token');
      const response = await fetch(
        `${window.location.origin}/api/gamification/goals`,
        {
          method: 'PUT',
          headers: {
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'application/json'
          },
          body: JSON.stringify({
            daily_goal: selectedGoal
          })
        }
      );

      if (response.ok) {
        await loadGoals();
        alert(language === 'fr' ? 'Objectif mis à jour !' : 'Goal updated!');
      }
    } catch (error) {
      console.error('Error updating goal:', error);
      alert(language === 'fr' ? 'Erreur lors de la mise à jour' : 'Error updating goal');
    } finally {
      setSaving(false);
    }
  };

  const getGoalDescription = (xp) => {
    if (xp <= 20) return language === 'fr' ? 'Détendu' : 'Relaxed';
    if (xp <= 50) return language === 'fr' ? 'Régulier' : 'Regular';
    if (xp <= 100) return language === 'fr' ? 'Sérieux' : 'Serious';
    return language === 'fr' ? 'Intense' : 'Intense';
  };

  const getGoalIcon = (xp) => {
    if (xp <= 20) return '🐌';
    if (xp <= 50) return '🚶';
    if (xp <= 100) return '🏃';
    return '🚀';
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-blue-50 via-purple-50 to-pink-50 flex items-center justify-center">
        <div className="text-xl">{language === 'fr' ? 'Chargement...' : 'Loading...'}</div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-blue-50 via-purple-50 to-pink-50 p-6">
      <div className="max-w-4xl mx-auto">
        {/* Header */}
        <div className="flex items-center justify-between mb-8">
          <Button
            variant="ghost"
            onClick={() => navigate('/dashboard')}
            className="flex items-center gap-2"
          >
            <ArrowLeft className="w-5 h-5" />
            {language === 'fr' ? 'Retour' : 'Back'}
          </Button>
          
          <h1 className="text-4xl font-bold text-gray-800 flex items-center gap-3">
            <Target className="w-10 h-10 text-blue-500" />
            {language === 'fr' ? 'Mon Objectif' : 'My Goal'}
          </h1>

          <div className="w-24"></div>
        </div>

        {/* Current Progress */}
        <Card className="mb-8 bg-white/80 backdrop-blur border-2 border-blue-200">
          <CardHeader>
            <CardTitle className="text-center">
              {language === 'fr' ? 'Progrès du jour' : "Today's Progress"}
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="space-y-4">
              <div className="flex justify-between items-center">
                <span className="text-2xl font-bold text-blue-600">
                  {goals?.daily_xp || 0} XP
                </span>
                <span className="text-gray-600">
                  / {goals?.daily_goal || 50} XP
                </span>
              </div>
              <Progress value={goals?.daily_progress || 0} className="h-4" />
              <p className="text-center text-sm text-gray-600">
                {goals?.daily_progress >= 100
                  ? (language === 'fr' ? '🎉 Objectif atteint !' : '🎉 Goal reached!')
                  : `${goals?.daily_progress || 0}% ${language === 'fr' ? 'complété' : 'completed'}`
                }
              </p>
            </div>
          </CardContent>
        </Card>

        {/* Goal Selection */}
        <Card className="bg-white/80 backdrop-blur border-2 border-purple-200">
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <Zap className="w-6 h-6 text-yellow-500" />
              {language === 'fr' ? 'Personnaliser mon objectif quotidien' : 'Customize Daily Goal'}
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-6">
            <div>
              <div className="flex justify-between items-center mb-4">
                <p className="text-lg font-semibold">
                  {language === 'fr' ? 'Objectif quotidien :' : 'Daily Goal:'}
                </p>
                <div className="text-right">
                  <p className="text-3xl font-bold text-purple-600">{selectedGoal} XP</p>
                  <p className="text-sm text-gray-600">
                    {getGoalIcon(selectedGoal)} {getGoalDescription(selectedGoal)}
                  </p>
                </div>
              </div>

              <div className="grid grid-cols-4 gap-3 mb-6">
                {goalOptions.map((xp) => (
                  <Button
                    key={xp}
                    variant={selectedGoal === xp ? 'default' : 'outline'}
                    className={`h-20 flex flex-col items-center justify-center transition-all ${
                      selectedGoal === xp
                        ? 'bg-purple-600 text-white scale-105 shadow-lg'
                        : 'hover:border-purple-400'
                    }`}
                    onClick={() => setSelectedGoal(xp)}
                  >
                    <span className="text-2xl mb-1">{getGoalIcon(xp)}</span>
                    <span className="font-bold">{xp} XP</span>
                  </Button>
                ))}
              </div>
            </div>

            {/* Info Section */}
            <div className="bg-blue-50 p-4 rounded-lg border border-blue-200">
              <h3 className="font-semibold mb-2 flex items-center gap-2">
                <TrendingUp className="w-5 h-5 text-blue-600" />
                {language === 'fr' ? 'Pourquoi définir un objectif ?' : 'Why set a goal?'}
              </h3>
              <ul className="text-sm text-gray-700 space-y-1">
                <li>• {language === 'fr' 
                  ? "Restez motivé et suivez vos progrès quotidiens" 
                  : "Stay motivated and track your daily progress"
                }</li>
                <li>• {language === 'fr' 
                  ? "Créez une habitude d'apprentissage régulière" 
                  : "Build a consistent learning habit"
                }</li>
                <li>• {language === 'fr' 
                  ? "Une leçon typique = 10-20 XP" 
                  : "A typical lesson = 10-20 XP"
                }</li>
              </ul>
            </div>

            <Button
              className="w-full h-12 bg-gradient-to-r from-purple-600 to-blue-600 hover:from-purple-700 hover:to-blue-700 text-white font-semibold"
              onClick={handleSaveGoal}
              disabled={saving || selectedGoal === goals?.daily_goal}
            >
              {saving
                ? (language === 'fr' ? 'Enregistrement...' : 'Saving...')
                : (language === 'fr' ? 'Enregistrer' : 'Save Goal')
              }
            </Button>
          </CardContent>
        </Card>
      </div>
    </div>
  );
};

export default GoalsPage;
